import { useState } from 'react'
import { X, ShieldOff, ShieldCheck, AlertTriangle, CheckCircle } from 'lucide-react'
import mapasService from '../../services/mapasService'
import toast from 'react-hot-toast'

const brl = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const IMPACTOS = [
  { label: 'Mapa do Comprador V1 e V2', desc: 'GTA/Nota removida dos totalizadores e médias' },
  { label: 'Fatura do Comprador', desc: 'GTA/Nota zerada no cálculo e no bloco de detalhes' },
  { label: 'Resumo da Fatura', desc: 'Total a pagar recalculado sem GTA/Nota' },
  { label: 'Recibo de Comissão', desc: 'Valor do recibo exclui GTA/Nota' },
  { label: 'Comissão por Cliente', desc: 'Despesas do comprador sem GTA/Nota no relatório' },
  { label: 'Gestão Financeira', desc: 'Conta a receber atualizada automaticamente' },
]

/**
 * Modal para isentar/reverter GTA e Nota — de forma independente — de um
 * comprador (escopo padrão) ou de um lote específico (escopo 'lote').
 *
 * Props:
 * - escopo: 'comprador' | 'lote' (default 'comprador')
 * - compradorNome, compradorId (escopo comprador)
 * - numeroLote, loteId (escopo lote)
 * - leilaoId, orgId
 * - gtaIsento, notaIsento: estado atual (booleans)
 * - totalGta, totalNota: valores cobrados hoje (opcional, informativo)
 * - onClose: () => void
 * - onSaved: () => void
 */
export default function IsentarGTAModal({
  escopo = 'comprador', compradorNome, compradorId, numeroLote, loteId, leilaoId, orgId,
  gtaIsento = false, notaIsento = false, totalGta = 0, totalNota = 0, totalGtaNota = 0,
  onClose, onSaved,
}) {
  const [saving, setSaving] = useState(false)
  // Pré-marcado como Isento: quem abre este modal normalmente veio pra isentar,
  // então já deixa os dois prontos pra só clicar Salvar. Quem já tava isento
  // continua isento (não muda nada, botão Salvar só ativa se algo mudar).
  const [gtaWant, setGtaWant] = useState(true)
  const [notaWant, setNotaWant] = useState(true)

  const isLote = escopo === 'lote'
  const alvoNome = isLote ? `Lote ${numeroLote}` : compradorNome
  const mudou = gtaWant !== !!gtaIsento || notaWant !== !!notaIsento

  const handleSalvar = async () => {
    if (!mudou) { onClose(); return }
    setSaving(true)
    try {
      // Componentes que passam a ser isentos e que voltam a ser cobrados
      const aIsentar = []
      const aReverter = []
      if (gtaWant !== !!gtaIsento) (gtaWant ? aIsentar : aReverter).push('gta')
      if (notaWant !== !!notaIsento) (notaWant ? aIsentar : aReverter).push('nota')

      const alvoDe = arr => (arr.length === 2 ? 'ambos' : arr[0])

      if (aIsentar.length) {
        const alvo = alvoDe(aIsentar)
        if (isLote) await mapasService.isentarGTALote(orgId, loteId, alvo)
        else await mapasService.isentarGTA(orgId, compradorId, leilaoId, alvo)
      }
      if (aReverter.length) {
        const alvo = alvoDe(aReverter)
        if (isLote) await mapasService.desfazerIsencaoGTALote(orgId, loteId, alvo)
        else await mapasService.desfazerIsencaoGTA(orgId, compradorId, leilaoId, alvo)
      }

      toast.success('Isenção de GTA/Nota atualizada')
      onSaved()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar isenção de GTA/Nota')
    } finally {
      setSaving(false)
    }
  }

  const Linha = ({ nome, valor, want, setWant, jaIsento }) => (
    <div className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
      jaIsento ? 'border-green-200 bg-green-50/60' : 'border-gray-200'
    }`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-gray-800">{nome}</p>
          {jaIsento && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
              <CheckCircle className="w-3 h-3" /> Já isento
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">
          {jaIsento
            ? `Isento — cobraria ${brl(valor)}`
            : (valor > 0 ? `Cobrando ${brl(valor)}` : 'Cobrando')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setWant(!want)}
        className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          want ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {want ? <><ShieldOff className="w-3.5 h-3.5" /> Isento</> : <><ShieldCheck className="w-3.5 h-3.5" /> Cobrar</>}
      </button>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-teal-600 flex-shrink-0">
          <ShieldOff className="w-5 h-5 text-white flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-semibold text-base truncate">Isentar GTA / Nota</h2>
            <p className="text-white/80 text-xs mt-0.5 truncate">{alvoNome}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo */}
        <div className="px-5 py-4 space-y-4 flex-1 overflow-y-auto">
          <p className="text-xs text-gray-500">
            Escolha o que isentar. GTA e Nota são independentes — dá para isentar só uma delas.
          </p>

          <div className="space-y-2">
            <Linha nome="GTA" valor={totalGta} want={gtaWant} setWant={setGtaWant} jaIsento={!!gtaIsento} />
            <Linha nome="Nota" valor={totalNota} want={notaWant} setWant={setNotaWant} jaIsento={!!notaIsento} />
          </div>

          {totalGtaNota > 0 && !gtaIsento && !notaIsento && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">GTA + Nota cobrados hoje</p>
                <p className="text-xl font-bold text-amber-700">{brl(totalGtaNota)}</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {isLote ? 'neste lote' : 'deste comprador neste leilão'}
                </p>
              </div>
            </div>
          )}

          {/* Lista de impactos */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Reflete em</p>
            <div className="space-y-1.5">
              {IMPACTOS.map((item, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg px-3 py-2 bg-gray-50">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            disabled={saving || !mudou}
            className="flex-1 px-4 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 bg-teal-600 hover:bg-teal-700"
          >
            {saving ? 'Aguarde...' : 'Salvar isenção'}
          </button>
        </div>
      </div>
    </div>
  )
}
